import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertCircle, ChevronRight, Loader2, Search } from 'lucide-react';
import { MainLayout } from '../layouts/MainLayout';
import { Meter } from '../components/Meter';
import { useParksDirectory } from '../hooks/useApi';
import { usePageMeta } from '../hooks/usePageMeta';
import type { DirectoryPark } from '../types';

type DirectoryFilter = 'all' | 'national' | 'live';
type DirectorySort = 'available' | 'booked' | 'name' | 'campgrounds';

const PAGE_SIZE = 60;

const FILTER_OPTIONS: Array<{ value: DirectoryFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'national', label: 'National Parks' },
  { value: 'live', label: 'With live data' },
];

function sortParks(parks: DirectoryPark[], sort: DirectorySort): DirectoryPark[] {
  if (sort === 'available') {
    // Server default: most-available-with-stats first, then alphabetical.
    return parks;
  }

  const sorted = [...parks];
  if (sort === 'booked') {
    sorted.sort((a, b) => {
      if (a.bookedPct === null && b.bookedPct === null) return a.name.localeCompare(b.name);
      if (a.bookedPct === null) return 1;
      if (b.bookedPct === null) return -1;
      return b.bookedPct - a.bookedPct;
    });
  } else if (sort === 'name') {
    sorted.sort((a, b) => a.name.localeCompare(b.name));
  } else {
    sorted.sort((a, b) => b.facilityCount - a.facilityCount);
  }
  return sorted;
}

export function DiscoveryPage() {
  const { data: parks, isLoading, error } = useParksDirectory();

  usePageMeta({
    title: 'All Parks — HellaCamping',
    description: 'Browse every park and camping area we track, with live bookability.',
    canonicalPath: '/discovery',
  });

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<DirectoryFilter>('all');
  const [sort, setSort] = useState<DirectorySort>('available');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchInput), 200);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Pagination cap, keyed to the current search/filter/sort so it resets to
  // the first page whenever the visible set changes shape (derived in render,
  // no effect needed).
  const viewKey = `${debouncedSearch}|${filter}|${sort}`;
  const [cap, setCap] = useState({ key: viewKey, count: PAGE_SIZE });
  const visibleCount = cap.key === viewKey ? cap.count : PAGE_SIZE;

  const filteredParks = useMemo(() => {
    if (!parks) return [];

    const needle = debouncedSearch.trim().toLowerCase();
    const matched = parks.filter((park) => {
      if (filter === 'national' && !park.isNationalPark) return false;
      if (filter === 'live' && park.bookedPct === null) return false;
      if (needle.length > 0 && !park.name.toLowerCase().includes(needle)) return false;
      return true;
    });

    return sortParks(matched, sort);
  }, [parks, debouncedSearch, filter, sort]);

  const visibleParks = filteredParks.slice(0, visibleCount);
  const remaining = filteredParks.length - visibleParks.length;

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Parks</h1>
          <p className="text-gray-600 mt-1">
            Browse {parks ? parks.length.toLocaleString() : ''} parks and camping areas — sorted
            so bookable trips surface first.
          </p>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-4" />
            <p className="text-gray-600">Loading parks…</p>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-700 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900">Failed to load parks</h2>
              <p className="text-red-800 text-sm mt-1">{error.message}</p>
            </div>
          </div>
        )}

        {!isLoading && !error && parks && (
          <>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search parks…"
                  className="w-56 rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFilter(option.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    filter === option.value
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}

              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as DirectorySort)}
                className="rounded-lg border-gray-200 text-sm bg-white border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                aria-label="Sort parks"
              >
                <option value="available">Most available</option>
                <option value="booked">Most booked</option>
                <option value="name">Name A–Z</option>
                <option value="campgrounds">Most campgrounds</option>
              </select>
            </div>

            <p className="text-sm text-gray-500">
              {filteredParks.length.toLocaleString()} park{filteredParks.length === 1 ? '' : 's'}
            </p>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4">
              <div className="divide-y divide-gray-100">
                {visibleParks.map((park) => (
                  <ParkRow key={park.placeId} park={park} />
                ))}
                {visibleParks.length === 0 && (
                  <p className="py-8 text-center text-sm text-gray-500">
                    No parks match your search.
                  </p>
                )}
              </div>
            </div>

            {remaining > 0 && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setCap({ key: viewKey, count: visibleCount + PAGE_SIZE })}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:border-emerald-300 hover:text-emerald-700"
                >
                  Show more ({remaining.toLocaleString()})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

function ParkRow({ park }: { park: DirectoryPark }) {
  return (
    <Link
      to="/park/$id"
      params={{ id: park.placeId }}
      className="flex items-center gap-4 py-3 group"
    >
      {park.imageUrl ? (
        <img
          src={park.imageUrl}
          alt=""
          loading="lazy"
          className="w-16 h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100"
        />
      ) : (
        <div className="w-16 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
      )}

      <div className="min-w-0">
        <p className="font-medium text-gray-900 line-clamp-1 group-hover:text-emerald-700">
          {park.name}
          {park.isNationalPark && (
            <span className="ml-2 align-middle text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
              National Park
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500">
          {park.facilityCount.toLocaleString()} campground{park.facilityCount === 1 ? '' : 's'}
          {park.originName ? ` · ${park.originName}` : ''}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-3 flex-shrink-0">
        {park.bookedPct !== null ? (
          <>
            <div className="w-24 hidden sm:block">
              <Meter pct={park.bookedPct} size="sm" tone="light" />
            </div>
            <span className="text-xs tabular-nums text-gray-600">
              {Math.round(park.bookedPct * 100)}% booked
            </span>
          </>
        ) : (
          <span className="text-xs text-gray-400">no live data</span>
        )}
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>
    </Link>
  );
}
